import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActionBar, Stack, Button, SheetContainer, Text } from "@bookingcom/bui-react";
import { CalendarIcon, CalendarDatesIcon, InfoSignIcon } from "@bookingcom/bui-assets-react/streamline";
import { ShellButton, Modal, ContentWithTitle } from "../Shell/index.js";
import { t } from "@bookingcom/lingojs-core";
import { Calendar, CalendarSelect } from "../Calendar/index.js";
import FareCalendarLoadingAnnouncement from "../FareCalendar/FareCalendarLoadingAnnouncement.js";
import { useFareCalendarEnabled } from "../FareCalendar/useFareCalendar.js";
import FareCalendarBadge from "../FareCalendar/FareCalendarBadge.js";
import useDate from "./useDate.js";
import styles from "./DateModal.module.css";
const errorId = "date_input_error";
const DateModal = (props) => {
    const { segmentIndex, isRebook = false } = props;
    const { errorMessage, from, getTextNights, i18n, isDateSelected, isRoundTrip, isMultiStop, setFromTo, text, to, trackV2, searchType, onTrackSBExperiment, isMobile, isDropdownOpen, trackCalendarOpenStage, fareCalendarDisclaimerText, fareCalendarConfig } = useDate({ segmentIndex });
    const fareCalendarEnabled = useFareCalendarEnabled(segmentIndex, fareCalendarConfig, "vertical");
    const fareCalendarDisplayEnabled = fareCalendarEnabled && !!fareCalendarConfig?.isFareCalendarDisplayEnabled;
    const buttonRef = useRef(null);
    const [showModal, setShowModal] = useState(false);
    const [fromKeyboard, setFromKeyboard] = useState(false);
    const [selectedDate, setSelectedDate] = useState({ from: from, to: to });
    const [showInfoSheet, setShowInfoSheet] = useState(false);
    const handleOnDateChange = useCallback((data) => {
        const { from, to } = data;
        setSelectedDate({ from, to });
    }, []);
    const closeModal = useCallback(() => {
        setShowModal(false);
        buttonRef.current?.focus();
    }, []);
    const handleOnClick = useCallback((e) => {
        e.stopPropagation();
        const isFromKeyboard = e.nativeEvent.isTrusted && e.nativeEvent.detail === 0 && !e.nativeEvent["pointerType"];
        setFromKeyboard(isFromKeyboard);
        setShowModal(true);
        trackV2("click_calendar");
    }, [from, isMultiStop, isRoundTrip, onTrackSBExperiment, to, trackV2, isMobile]);
    const handleOnClose = useCallback(() => {
        closeModal();
        setFromKeyboard(false);
    }, [closeModal]);
    const handleOnDoneClick = useCallback(() => {
        closeModal();
        setFromKeyboard(false);
        trackV2("click_calendar_done");
        setFromTo({ from: selectedDate.from, to: selectedDate.to });
    }, [closeModal, selectedDate.from, selectedDate.to, setFromTo, trackV2]);
    const disabledCTA = useMemo(() => {
        const { from, to } = selectedDate;
        return isRoundTrip ? !from || !to : !from;
    }, [isRoundTrip, selectedDate]);
    useEffect(() => {
        setTimeout(() => {
            setShowModal(isDropdownOpen || false);
        }, 0);
    }, [isDropdownOpen]);
    useEffect(() => {
        if (showModal) {
            trackCalendarOpenStage();
        }
    }, [showModal, trackCalendarOpenStage]);
    useEffect(() => {
        setSelectedDate({ from: from, to: to });
    }, [from, to, setSelectedDate]);
    const titleValue = useMemo(() => {
        if (isRebook) {
            return searchType === "ROUNDTRIP"
                ? i18n.trans(t("flights_pb_rebook_calendar_prompt_rt"))
                : i18n.trans(t("flights_pb_rebook_calendar_prompt_ow"));
        }
        else {
            return searchType === "ROUNDTRIP"
                ? i18n.trans(t("flights_searchbox_travel_dates"))
                : i18n.trans(t("flights_searchbox_travel_date"));
        }
    }, [isRebook, searchType, i18n]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ShellButton, { icon: isDateSelected ? CalendarDatesIcon : CalendarIcon, errorMessage: errorMessage, errorVariant: "inline", ref: buttonRef, onClick: handleOnClick, "data-ui-name": `button_date_segment_${segmentIndex}`, "data-ui-error": Boolean(errorMessage), size: "large", errorId: errorId, halfVerticalPadding: true, alignIcon: "center", "aria-invalid": !!errorMessage, "aria-describedby": searchType === "MULTISTOP" ? `a11y_multi_segment_description_${segmentIndex}` : undefined },
            React.createElement(ContentWithTitle, { title: titleValue, content: text })),
        React.createElement(Modal, { active: showModal, mountMode: "on-demand", onAfterClose: handleOnClose, onClose: handleOnClose, onCloseTrigger: handleOnClose, position: "fullScreen", title: titleValue, className: styles.modal, footer: React.createElement(ActionBar, { topContent: getTextNights(selectedDate), button: {
                    wide: true,
                    onClick: handleOnDoneClick,
                    disabled: disabledCTA,
                    text: i18n.trans(t("flights_action_done")),
                    size: "large",
                    attributes: {
                        "data-ui-name": "button_calendar_action_bar_done"
                    }
                } }) },
            React.createElement(React.Fragment, null,
                React.createElement(FareCalendarLoadingAnnouncement, { segmentIndex: segmentIndex, fareCalendarConfig: fareCalendarConfig, mode: "vertical" }),
                fromKeyboard ? (React.createElement(CalendarSelect, { onDateChange: handleOnDateChange, segmentIndex: segmentIndex })) : (React.createElement(React.Fragment, null,
                    fareCalendarDisplayEnabled && fareCalendarConfig?.currencyCode && (React.createElement(Stack, { direction: "row", gap: 2, justifyContent: "space-between", alignItems: "center" },
                        React.createElement(FareCalendarBadge, { currencyCode: fareCalendarConfig.currencyCode }),
                        React.createElement(Button, { variant: "tertiary-neutral", icon: React.createElement(InfoSignIcon, null), onClick: () => setShowInfoSheet(true) }))),
                    React.createElement(Calendar, { mode: "vertical", onDateChange: handleOnDateChange, segmentIndex: segmentIndex, className: styles.calendar }))))),
        React.createElement(SheetContainer, { title: i18n.trans(t("flights_mdot_fare_cal_about")), active: showInfoSheet, onCloseTrigger: () => setShowInfoSheet(false), ariaLabel: i18n.trans(t("a11y_flights_mdot_fare_cal_tooltip")), closeAriaLabel: i18n.trans(t("close")), titleTagName: "h2" },
            React.createElement(Text, { variant: "body_1" }, fareCalendarDisclaimerText))));
};
export default DateModal;
